// Shared pre-inspection roster + readiness helpers.
//
// Originally local to src/screens/PreInspection.tsx; lifted here so other
// surfaces (Practice Match creation on Match Control, which only offers
// teams that have COMPLETED pre-inspection) compute readiness with exactly
// the same roster + canonical-event rules the Pre-Inspection screen uses.
//
// The pre-inspection store is keyed per-event (preInspectionLevel[team]
// [event][robot]) for parity with the live desk's rollup, but each physical
// robot has ONE canonical event it reads/writes under — see
// canonicalEventForRobot. A team is "pre-inspection complete" when every
// robot in their unified roster is at l3 under its canonical event key.

import type { MatchEvent, QueueItem, Team } from '../types';
import { ROBOTS_BY_EVENT } from '../types';

// A robot's pre-inspection level, derived from its pre-inspection queue row's
// per-station progress (pre-inspection runs through the station desk, no L3,
// so 'l3' just means "cleared all three stations"). 'pending' if not enqueued.
export function preInspectionLevelOf(
  queue: readonly QueueItem[],
  teamId: string,
  robotClass: string,
): 'pending' | 'l1' | 'l2' | 'l3' | 'failed' {
  const row = queue.find((q) => q.preInspection && q.team === teamId && q.robotClass === robotClass);
  if (!row) return 'pending';
  if (row.failed) return 'failed';
  const n = row.passedStations.length;
  return n >= 3 ? 'l3' : n === 2 ? 'l2' : n === 1 ? 'l1' : 'pending';
}

// Pick the event key under which (team, robot) state lives. Engineer is
// only ever pre-inspected for EC; combat robots are pre-inspected once
// per physical robot, so we collapse 3v3 + 1v1 onto whichever event the
// team plays (preferring 3v3 if both, since 3v3 robots are the broader
// set).
export function canonicalEventForRobot(team: Team, robot: string): MatchEvent {
  if (robot === 'Engineer') return 'ec';
  if (team.events.includes('3v3')) return '3v3';
  if (team.events.includes('1v1')) return '1v1';
  // Combat robot on an EC-only team is degenerate (no eligible event);
  // fall through to 'ec' so reads/writes don't blow up — the row won't
  // include this robot in the first place.
  return 'ec';
}

// Union of pre-inspectable robot classes for a team across all the
// events they're registered for. Order: combat robots in the team's own
// declared order, then Engineer if they play EC. Each robot is paired
// with the canonical event it should read/write state under.
export function robotsForTeam(team: Team): { robots: string[]; events: MatchEvent[] } {
  const robots: string[] = [];
  const events: MatchEvent[] = [];
  const playsCombat = team.events.some((ev) => ev === '3v3' || ev === '1v1');
  if (playsCombat) {
    for (const r of team.robots) {
      const inEvent = team.events.some((ev) => {
        if (ev === 'EC') return false;
        return ROBOTS_BY_EVENT[ev as MatchEvent].includes(r);
      });
      if (inEvent) {
        robots.push(r);
        events.push(canonicalEventForRobot(team, r));
      }
    }
  }
  if (team.events.includes('EC')) {
    robots.push('Engineer');
    events.push('ec');
  }
  return { robots, events };
}

// True when every robot in the team's unified roster has cleared all three
// pre-inspection stations (l3) under its canonical event key. This is the
// same calculation as the Pre-Inspection screen's per-row `ready` flag.
// Teams with an empty roster are never "complete" (nothing was inspected).
export function isPreInspectionComplete(queue: readonly QueueItem[], team: Team): boolean {
  const { robots } = robotsForTeam(team);
  if (robots.length === 0) return false;
  return robots.every((robot) => preInspectionLevelOf(queue, team.id, robot) === 'l3');
}

// Event-scoped LEGAL-LINEUP readiness for practice — a team needs enough of its
// robots pre-inspected to field a LEGAL lineup, NOT the full lineup:
//   1v1 → the Infantry (Standard) cleared. A 3v3 team whose Hero/Sentry are
//         still pending can play a 1v1 with their cleared Infantry.
//   3v3 → at least 2 of its 3 robots (∩ Standard/Hero/Sentry) cleared — 2/3 is a
//         legal 3v3 lineup, so a team that passed with 2 robots can practice.
//         The EC Engineer is never required for a combat event.
// Levels are read under each robot's CANONICAL event key (see
// canonicalEventForRobot) — a both-events team's Standard lives under '3v3'.
export function isPreInspectionCompleteForEvent(
  queue: readonly QueueItem[],
  team: Team,
  event: '3v3' | '1v1',
): boolean {
  const { robots } = robotsForTeam(team);
  const required = robots.filter((robot) =>
    event === '1v1' ? robot === 'Standard' : ROBOTS_BY_EVENT['3v3'].includes(robot));
  if (required.length === 0) return false;
  const cleared = required.filter((robot) => preInspectionLevelOf(queue, team.id, robot) === 'l3').length;
  // 1v1 fields one robot (the Standard); 3v3 can field as few as 2 of 3.
  return cleared >= (event === '1v1' ? 1 : 2);
}
